import React, { useEffect, useRef, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import dayjs from "dayjs";
import { Badge, Button } from "antd";
import {
  HomeOutlined,
  PlusOutlined,
  EditOutlined,
  DownloadOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { NavLink, useNavigate, useSearchParams } from "react-router";
import Table from "../../Common/Table";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import {
  ActionType,
  RequestData,
  RequestOptionsType,
} from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import {
  useSurveysTableGet,
  SurveyTableRow,
  SurveysGetParams,
  SurveyStatus,
  SurveyStatusDisplay,
  SurveyStatusColor,
  useSurveyDownload,
} from "../../../hooks/api/surveys";
import { getSorterParams } from "../../../utils";
import { dateTimeFormat } from "../../../contstant";
import DownloadButton from "./actions";

export function getSurveyStatusColor(status: SurveyStatus): string {
  return SurveyStatusColor[status] || "rgba(0,0,0,.15)";
}

function ToolBar() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return [
    <Button
      key="add"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={() => navigate("create")}
    >
      Add
    </Button>,
  ];
}

let userCache: RequestOptionsType[] | null = null;

export default function SurveysContent(): React.ReactNode | null {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const surveysGet = useSurveysTableGet();
  const actionRef = useRef<ActionType>(null);
  const surveyDownload = useSurveyDownload();

  const loadUserOptions = async (
    reviewTableGet: ReturnType<typeof useSurveysTableGet>,
  ): Promise<RequestOptionsType[]> => {
    if (userCache) return userCache;

    const response = await reviewTableGet.fetch({
      page: 1,
      pageSize: 50,
    });

    if (!response?.data) return [];

    const map = new Map<string, RequestOptionsType>();

    response.data.forEach((item) => {
      const user = item.user;
      if (user?.id) {
        map.set(user.id, {
          label: `${user.firstName} ${user.lastName}`,
          value: user.id,
        });
      }
    });

    // eslint-disable-next-line react-hooks/globals
    userCache = Array.from(map.values());

    return userCache;
  };

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Surveys" },
    ]);
  }, [i18n.language, t]);

  const columns = (t: TFunction): ProColumns<SurveyTableRow>[] => [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      hideInSearch: true,
      ellipsis: true,
    },
    {
      title: "Order ID",
      dataIndex: "orderId",
      key: "orderId",
      // sorter: true,
      hideInSearch: true,
      render: (_, record) => {
        if (!record.jobs || record.jobs.length === 0) return "-";
        return record.jobs.map((job) => job.jobId).join(", ");
      },
    },
    {
      title: "Created by",
      dataIndex: "userName",
      key: "userName",
      sorter: true,
      hideInSearch: true,
      render: (text, record) => (
        <NavLink
          to={`/users/${record.user?.id}/edit`}
          style={{ textDecoration: "underline" }}
        >
          {record.user?.firstName} {record.user?.lastName}
        </NavLink>
      ),
    },
    {
      title: "Created by",
      dataIndex: "createdBy",
      key: "createdBy",
      valueType: "select",
      hideInTable: true,
      request: () => loadUserOptions(surveysGet),
      fieldProps: {
        showSearch: true,
        filterOption: true,
      },
      search: {
        transform: (value) => ({
          userId: value,
        }),
      },
      render: (_, record) => (
        <div>
          {record.user?.firstName} {record.user?.lastName}
        </div>
      ),
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      hideInSearch: true,
      renderText: (text) => dayjs(text).format(dateTimeFormat),
      ellipsis: true,
    },
    {
      title: "Last updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      sorter: true,
      hideInSearch: true,
      renderText: (text) => dayjs(text).format(dateTimeFormat),
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      valueEnum: SurveyStatusDisplay,
      render: (_, record) => (
        <Badge
          color={getSurveyStatusColor(record.status)}
          text={
            <span className="capitalize-first-letter">{record.status}</span>
          }
        />
      ),
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      valueType: "dateRange",
      hideInTable: true,
      search: {
        transform: (value?: [string, string]) => {
          if (!value || value.length !== 2) return {};

          return {
            createdFrom: value[0],
            createdTo: value[1],
          };
        },
      },
    },
    {
      title: "Last updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      sorter: true,
      valueType: "dateRange",
      hideInTable: true,
      search: {
        transform: (value?: [string, string]) => {
          if (!value || value.length !== 2) return {};

          return {
            updatedFrom: value[0],
            updatedTo: value[1],
          };
        },
      },
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, any> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<SurveyTableRow>>> => {
    const newParams: SurveysGetParams = {
      page: current || 1,
      pageSize: pageSize || 10,
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return surveysGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;
        return {
          data: data || [],
          success: true,
          total,
        };
      }
      return { data: [], success: false, total: 0 };
    });
  };

  return (
    <Table<SurveyTableRow>
      key={`${tableKey}-${i18n.language}`}
      resetKey={() => setTableKey(Date.now())}
      className={clsx("transparent")}
      headerTitle="List of Surveys"
      toolBarRender={ToolBar}
      request={tableRequest}
      columns={columns(t)}
      actionRef={actionRef}
      actions={[
        [
          "edit",
          ({ record }) => ({
            title: "Edit survey",
            onClick: (e: MouseEvent) => {
              e.preventDefault();
              const currentPage = actionRef.current?.pageInfo?.current;
              navigate(`${record.id}/edit`, {
                state: { fromPage: currentPage?.toString() },
              });
            },
          }),
        ],
        [
          DownloadButton,
          {
            setTableKey,
          },
        ],
      ]}
      search={{
        style: {
          // display: "none",
        },
      }}
      options={{
        search: {
          name: "search",
          placeholder: "Search",
          style: { width: 296 },
        },
      }}
      columnsState={{
        persistenceKey: "pro-table-field-technicians",
        persistenceType: "localStorage",
      }}
    />
  );
}
